import axios from 'axios'
import { from } from 'rxjs'

import {
  AuthAPI,
  CustomerAPI,
  CustomerAPIWithRetry,
  KongCustomerAPI,
  WidgetCacheAPI,
} from 'api'
import config from 'config'
import {
  checkStatus,
  pluckData,
  rxCheckStatus,
  rxPluckData,
  rxRetryWithBackoff,
} from 'utils/api'
import * as recaptcha from 'utils/recaptcha'

export const api = {
  create(settings, enableDefaultWelcomeMessages = false, captchaToken = '') {
    return from(
      CustomerAPIWithRetry.post('/organizations', settings, {
        params: {
          enableDefaultWelcomeMessages,
          useNetsuite: true,
          captchaToken,
          clientId: config.CLIENT_ID,
        },
        transformRequest: [
          (data, headers) => {
            delete headers.common['Authorization']
            return data
          },
          ...axios.defaults.transformRequest,
        ],
      })
    ).pipe(rxCheckStatus(), rxPluckData())
  },
  trackSignup(properties) {
    return from(
      CustomerAPI.post('/organizations/track_signup', properties)
    ).pipe(rxCheckStatus(), rxPluckData())
  },
  fetch(orgId) {
    return from(CustomerAPI.get(`/organizations/${orgId}`)).pipe(
      rxCheckStatus(),
      rxPluckData()
    )
  },
  fetchUsers(orgId) {
    return from(
      CustomerAPI.get(
        `/organizations/${orgId}/users?status=INVITED&status=ENABLED&status=ARCHIVED`
      )
    ).pipe(rxCheckStatus(), rxPluckData())
  },
  checkUniqueHandle(handle) {
    // TODO: Verify head in Axios
    return from(KongCustomerAPI.head('/organizations/handle_check', { handle }))
  },
  updateProperties(id, properties) {
    return from(
      CustomerAPI.patch(`/organizations/${id}/properties`, properties)
    ).pipe(rxCheckStatus(), rxRetryWithBackoff(), rxPluckData())
  },
  updateEmbed(operations) {
    return from(
      WidgetCacheAPI.patch('/embeds/_default/configuration', operations)
    ).pipe(rxCheckStatus(), rxRetryWithBackoff())
  },
  updateEmbedPromise(operations) {
    return WidgetCacheAPI.patch('/embeds/_default/configuration', operations)
      .then(checkStatus())
      .then(pluckData())
  },
  updateOrg(id, update) {
    return from(CustomerAPI.patch(`/organizations/${id}`, update)).pipe(
      rxCheckStatus(),
      rxRetryWithBackoff()
    )
  },
  fetchOrgProperties(orgId) {
    return from(CustomerAPI.get(`/organizations/${orgId}/properties`)).pipe(
      rxCheckStatus(),
      rxPluckData()
    )
  },
  shareEmailSnippet(email) {
    return CustomerAPI.post(`/snippets/send?email=${email}`)
      .then(checkStatus())
      .then(pluckData())
  },
  shareEmailSnippetExp(email) {
    return CustomerAPI.post(`/snippets/send-exp?email=${email}`)
      .then(checkStatus())
      .then(pluckData())
  },
  fetchEmbed() {
    return WidgetCacheAPI.get('/embeds/_default')
      .then(checkStatus())
      .then(pluckData())
  },
  rxFetchEmbed() {
    return from(WidgetCacheAPI.get('/embeds/_default')).pipe(
      rxCheckStatus(),
      rxRetryWithBackoff(),
      rxPluckData()
    )
  },
  exchangeGoogleCodeForDriftCode(payloadProps) {
    const payload = {
      code: payloadProps.code,
      clientId: config.DEFAULT_CLIENT_ID,
      anonymousId:
        !!window.analytics && !!window.analytics.user
          ? window.analytics.user().anonymousId()
          : 'none',
      redirectUri: `${config.LETS_GO_URL}/letsgo${payloadProps.pathname}`,
    }
    return from(
      AuthAPI.post('/google/oauth/acceptCustomRedirect', payload)
    ).pipe(rxCheckStatus(), rxRetryWithBackoff(), rxPluckData())
  },
  exchangeMicrosoftCodeForDriftCode(payloadProps) {
    const payload = {
      code: payloadProps.code,
      clientId: config.DEFAULT_CLIENT_ID,
      anonymousId:
        !!window.analytics && !!window.analytics.user
          ? window.analytics.user().anonymousId()
          : 'none',
      redirectUri: `${config.LETS_GO_URL}/letsgo${payloadProps.pathname}`,
    }
    return from(
      AuthAPI.post('/microsoft/oauth/acceptCustomRedirect', payload)
    ).pipe(rxCheckStatus(), rxRetryWithBackoff(), rxPluckData())
  },
  verifyEmail(payloadProps) {
    const clientId = config.CLIENT_ID
    const captchaToken = recaptcha.getToken()
    const payload = {
      email: payloadProps.email,
      name: payloadProps.name,
      password: payloadProps.password,
      configuration: {},
    }

    return CustomerAPI.post(
      `/organizations/requestCreate?clientId=${clientId}&captchaToken=${captchaToken}&enableDefaultWelcomeMessages=false&useNetsuite=true`,
      payload
    )
  },
  resendVerificationEmail(email) {
    return CustomerAPI.post('/email/resend', email)
  },
  verifyToken(relayToken) {
    return CustomerAPI.post(`/organizations/createOrg?relayToken=${relayToken}`)
  },
}
